import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { clsx } from 'clsx';
import type { ReactElement } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = AriaButtonProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Elevated primary: a brand glow that lifts on hover + settles on press,
  // dropped entirely when disabled so a dead button never looks raised.
  primary:
    'bg-primary-500 text-white shadow-[var(--shadow-brand-glow)] hover:bg-primary-900 hover:shadow-[var(--shadow-brand-glow-hover)] data-[pressed]:bg-primary-900 data-[pressed]:shadow-[var(--shadow-xs)] data-[disabled]:bg-neutral-300 data-[disabled]:text-neutral-500 data-[disabled]:shadow-none',
  secondary:
    'border border-primary-500 text-primary-500 shadow-[var(--shadow-xs)] hover:bg-primary-50 data-[pressed]:bg-primary-100 data-[disabled]:border-neutral-300 data-[disabled]:text-neutral-400 data-[disabled]:shadow-none',
  ghost:
    'text-primary-500 hover:bg-neutral-100 data-[pressed]:bg-neutral-200 data-[disabled]:text-neutral-400',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  ...rest
}: ButtonProps): ReactElement {
  return (
    <AriaButton
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-[color,background-color,box-shadow,transform] duration-150',
        'data-[pressed]:scale-[0.98] motion-reduce:data-[pressed]:scale-100',
        'data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary-500',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        typeof className === 'string' ? className : undefined,
      )}
    />
  );
}
